(() => {
  const money = (cents) => {
    if (typeof cents !== "number") return "";
    return (cents / 100).toFixed(2);
  };

  const uniqBy = (arr, keyFn) => {
    const seen = new Set();
    const out = [];
    for (const item of arr) {
      const k = keyFn(item);
      if (seen.has(k)) continue;
      seen.add(k);
      out.push(item);
    }
    return out;
  };

  const clamp = (n, min, max) => Math.max(min, Math.min(max, n));

  // ---------------------------------------------------------------------------
  // AJAX product loader — replaces the old base64 Liquid approach.
  // Fetches /collections/[handle]/products.json with pagination (250/page).
  // Shopify's JSON API has no 50-product cap unlike collection.products in Liquid.
  // ---------------------------------------------------------------------------
  const FETCH_LIMIT = 250;

  function mapProduct(p, colTitle) {
    const v = (p.variants && p.variants[0]) || {};
    let img = null;
    if (p.images && p.images.length > 0) img = p.images[0].src || null;

    // Shopify returns price as a string like "299.00" in products.json
    const price = v.price ? Math.round(parseFloat(v.price) * 100) : 0;

    return {
      id: p.id,
      title: p.title || "",
      handle: p.handle || "",
      url: "/products/" + (p.handle || ""),
      image: img,
      price,
      variant_id: v.id || null,
      vendor: p.vendor || "",
      collection_title: colTitle,
      // products.json ger oss available men INTE inventory_management/policy
      // dessa berikas efteråt via Liquid-lookup i loadAllProducts()
      available: v.available === true,
      inventory_management: null,  // fylls i av enrichFromLiquid()
      inventory_policy: null,       // fylls i av enrichFromLiquid()
    };
  }

  // Läs inventory lookup-tabellen från Liquid-renderad JSON i DOM
  function readInvLookup(root) {
    const el = root.querySelector("[data-bb3x-inv]");
    if (!el) return {};
    try {
      return JSON.parse(el.textContent.trim()) || {};
    } catch (e) {
      console.warn("[bb3x] Could not parse inventory lookup", e);
      return {};
    }
  }

  // Berika produkter med inventory_management + inventory_policy från Liquid-lookup
  function enrichFromLiquid(products, invLookup) {
    for (const p of products) {
      const key = String(p.variant_id);
      const entry = invLookup[key];
      if (entry) {
        p.inventory_management = entry.management || null;
        p.inventory_policy = entry.policy || "deny";
      } else {
        // Fallback om variant saknas i lookup (produkt utanför Liquid 200-gränsen):
        // Om available=false och vi inte vet policy, anta sold_out (säkrast för butiken)
        p.inventory_management = p.available ? null : "shopify";
        p.inventory_policy = "deny";
      }
    }
    return products;
  }

  async function fetchCollectionProducts(handle, title) {
    const products = [];
    let page = 1;
    let hasMore = true;

    while (hasMore) {
      try {
        const res = await fetch(
          `/collections/${handle}/products.json?limit=${FETCH_LIMIT}&page=${page}`,
          { headers: { Accept: "application/json" } }
        );

        if (!res.ok) {
          console.warn(`[bb3x] HTTP ${res.status} fetching collection "${handle}" page ${page}`);
          break;
        }

        const data = await res.json();
        const batch = (data.products || []).map((p) => mapProduct(p, title));
        products.push(...batch);

        hasMore = batch.length === FETCH_LIMIT;
        page++;
      } catch (err) {
        console.warn(`[bb3x] Network error fetching collection "${handle}":`, err);
        break;
      }
    }

    return products;
  }

  async function loadAllProducts(root) {
    // Read Liquid inventory lookup first (policy + management per variant_id)
    const invLookup = readInvLookup(root);

    // Try new AJAX data source first
    const colEl = root.querySelector("[data-bb3x-collections]");
    if (colEl) {
      let collections = [];
      try {
        collections = JSON.parse(colEl.textContent.trim());
      } catch (e) {
        console.warn("[bb3x] Could not parse collections JSON", e);
      }

      if (Array.isArray(collections) && collections.length) {
        const results = await Promise.all(
          collections
            .filter((c) => c && c.handle)
            .map((c) => fetchCollectionProducts(c.handle, c.title || c.handle))
        );

        const seen = {};
        const all = [];
        for (const batch of results) {
          for (const p of batch) {
            const key = String(p.variant_id);
            if (!seen[key]) {
              seen[key] = true;
              all.push(p);
            }
          }
        }
        // Berika med inventory_policy + management från Liquid
        return enrichFromLiquid(all, invLookup);
      }
    }

    // Fallback: old base64 payload (backwards compat)
    const b64El = root.querySelector("[data-bb3x-products-b64]");
    if (b64El) {
      console.warn("[bb3x] Falling back to base64 payload — upgrade .liquid to use data-bb3x-collections for full product counts.");
      const b64 = (b64El.textContent || "").replace(/\s+/g, "");
      const decoded = decodeB64(b64);
      const result = safeParseArrayJSON(decoded);
      if (result.ok && Array.isArray(result.data)) return result.data;
    }

    return [];
  }

  // Legacy helpers kept for backwards-compat fallback
  const decodeB64 = (b64) => {
    try {
      const clean = String(b64 || "").replace(/\s+/g, "");
      const bin = atob(clean);
      const bytes = Uint8Array.from(bin, (c) => c.charCodeAt(0));
      return new TextDecoder("utf-8").decode(bytes);
    } catch (e) {
      console.warn("[bb3x] base64 decode failed", e);
      return "";
    }
  };

  const safeParseArrayJSON = (raw) => {
    const txt = String(raw || "").trim();
    if (!txt) return { ok: true, data: [] };
    try {
      const parsed = JSON.parse(txt);
      return { ok: true, data: parsed };
    } catch (_) {}
    const start = txt.indexOf("[");
    const end = txt.lastIndexOf("]");
    if (start !== -1 && end !== -1 && end > start) {
      const slice = txt.slice(start, end + 1);
      try {
        return { ok: true, data: JSON.parse(slice), usedFallback: true };
      } catch (e) {
        return { ok: false, error: e };
      }
    }
    return { ok: false, error: new Error("Could not locate JSON array in payload.") };
  };

  // ---------------------------------------------------------------------------

  class BundleBuilder3X {
    constructor(root) {
      this.root = root;

      this.discountType = (root.getAttribute("data-discount-type") || "percent").toLowerCase();
      this.discountValue = Number(root.getAttribute("data-discount-value") || 0);

      this.products = [];
      this.selected = [];

      this.slotsWrap = root.querySelector(".bb3x__slots");
      this.addSlotBtn = root.querySelector("[data-bb3x-add-slot]");
      this.addToCartBtn = root.querySelector("[data-bb3x-add-to-cart]");
      this.statusEl = root.querySelector("[data-bb3x-status]");

      this.totalOldEl = root.querySelector("[data-bb3x-total-old]");
      this.totalNewEl = root.querySelector("[data-bb3x-total-new]");

      this.modal = root.querySelector("[data-bb3x-modal]");
      this.modalCloseEls = root.querySelectorAll("[data-bb3x-close]");
      this.modalList = root.querySelector("[data-bb3x-list]");
      this.modalSearch = root.querySelector("[data-bb3x-search]");
      this.modalMeta = root.querySelector("[data-bb3x-result-meta]");
      this.modalTitle = root.querySelector("[data-bb3x-modal-title]");

      this.modalFilter = root.querySelector("[data-bb3x-filter]");
      this.activeCollectionFilter = "";

      this.activeSlotIndex = null;
      this.lastFocus = null;

      this.init();
    }

    async init() {
      // Show loading state while fetching
      this.flashStatus("Laddar produkter…");

      try {
        const raw = await loadAllProducts(this.root);
        this.products = uniqBy(
          Array.isArray(raw) ? raw : [],
          (p) => String(p && p.variant_id)
        ).filter((p) => p && p.variant_id && typeof p.price === "number");
      } catch (err) {
        console.warn("[bb3x] Failed to load products", err);
        this.products = [];
      }

      this.buildCollectionFilterOptions();
      this.bindEvents();
      this.updateState();
    }

    bindEvents() {
      this.root.querySelectorAll("[data-bb3x-slot]").forEach((btn) => {
        btn.addEventListener("click", () => {
          const idx = parseInt(btn.getAttribute("data-index"), 10);
          this.openPicker(idx);
        });
      });

      this.addSlotBtn?.addEventListener("click", () => this.addSlot());
      this.addToCartBtn?.addEventListener("click", () => this.addToCart());

      this.modalCloseEls.forEach((el) => el.addEventListener("click", () => this.closeModal()));

      document.addEventListener("keydown", (e) => {
        if (this.isModalOpen() && e.key === "Escape") this.closeModal();
      });

      this.modalSearch?.addEventListener("input", () => this.renderModalList());
      this.modalSearch?.addEventListener("keydown", (e) => {
        if (e.key === "Enter") e.preventDefault();
      });

      this.modalFilter?.addEventListener("change", () => {
        this.activeCollectionFilter = this.modalFilter.value || "";
        this.renderModalList(true);
      });
    }

    buildCollectionFilterOptions() {
      if (!this.modalFilter) return;

      this.modalFilter.innerHTML = "";

      const optAll = document.createElement("option");
      optAll.value = "";
      optAll.textContent = "Alla kollektioner";
      this.modalFilter.appendChild(optAll);

      const cols = new Map();
      for (const p of this.products) {
        const title = (p.collection_title || "").trim();
        if (!title) continue;
        const key = title.toLowerCase();
        if (!cols.has(key)) cols.set(key, title);
      }

      const sorted = Array.from(cols.values()).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: "base" })
      );

      for (const label of sorted) {
        const opt = document.createElement("option");
        opt.value = label;
        opt.textContent = label;
        this.modalFilter.appendChild(opt);
      }
    }

    // Returns one of: "sold_out" | "preorder" | "in_stock"
    // Logik:
    //   available=true                          → in_stock  (Shopify sätter detta korrekt)
    //   available=false + policy=continue       → preorder  (tillåts sälja vid slut)
    //   available=false + policy=deny (default) → sold_out  (blockeras)
    //   available=false + management=null       → in_stock  (ej spårat = alltid säljbart)
    stockStatus(p) {
      // Ej spårat lager (management=null) — alltid tillgänglig
      if (!p.inventory_management) return "in_stock";

      // Spårat lager och tillgänglig
      if (p.available === true) return "in_stock";

      // Spårat lager, ej tillgänglig — skilja på policy
      if (p.inventory_policy === "continue") return "preorder";
      return "sold_out";
    }

    maxQtyForProduct(p) {
      const status = this.stockStatus(p);
      if (status === "sold_out") return 0;
      if (status === "preorder") return Infinity;
      // in_stock — Infinity eftersom products.json inte ger oss exakt antal
      return Infinity;
    }

    countSelected(variantId, excludeSlotIndex = null) {
      let c = 0;
      for (let i = 0; i < this.selected.length; i++) {
        if (excludeSlotIndex !== null && i === excludeSlotIndex) continue;
        const it = this.selected[i];
        if (it && String(it.variant_id) === String(variantId)) c++;
      }
      return c;
    }

    canSelectProduct(p, slotIndex) {
      const max = this.maxQtyForProduct(p);
      if (max === Infinity) return true;
      const used = this.countSelected(p.variant_id, slotIndex);
      return used + 1 <= max;
    }

    calcDiscountedCents(originalCents) {
      const price = typeof originalCents === "number" ? originalCents : 0;
      if (!this.discountValue || this.discountValue <= 0) return price;

      if (this.discountType === "fixed") {
        const off = Math.round(this.discountValue * 100);
        return Math.max(0, price - off);
      }

      const pct = Math.max(0, Math.min(100, this.discountValue));
      return Math.max(0, Math.round(price * (1 - pct / 100)));
    }

    addSlot() {
      const idx = this.getSlotCount();
      const isRequired = idx < 3;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "bb3x__slot";
      btn.setAttribute("data-bb3x-slot", "");
      btn.setAttribute("data-index", String(idx));
      btn.setAttribute("aria-haspopup", "dialog");

      btn.innerHTML = `
        <span class="bb3x__slotInner">
          <span class="bb3x__slotTop">
            <span class="bb3x__slotLabel">Parfym</span>
            <span class="bb3x__slotReq">${isRequired ? "Required" : "Optional"}</span>
          </span>
          <span class="bb3x__slotCta">Välj</span>
          <span class="bb3x__slotHint">Tryck för att välja doft</span>
        </span>
      `;

      btn.addEventListener("click", () => this.openPicker(idx));
      this.slotsWrap.appendChild(btn);

      this.updateState();
      this.flashStatus("Ny slot tillagd.");
    }

    getSlotCount() {
      return this.slotsWrap ? this.slotsWrap.children.length : 0;
    }

    openPicker(slotIndex) {
      if (!this.products.length) {
        this.flashStatus("Inga produkter hittades i valda kollektioner.");
        return;
      }

      this.activeSlotIndex = slotIndex;
      this.lastFocus = document.activeElement;

      if (this.modalTitle) this.modalTitle.textContent = `Välj parfym för slot ${slotIndex + 1}`;

      if (this.modalSearch) this.modalSearch.value = "";
      if (this.modalFilter) this.modalFilter.value = this.activeCollectionFilter || "";

      this.modal.removeAttribute("hidden");
      document.documentElement.style.overflow = "hidden";

      this.renderModalList(true);
      requestAnimationFrame(() => this.modalSearch?.focus());
    }

    closeModal() {
      if (!this.isModalOpen()) return;
      this.modal.setAttribute("hidden", "");
      document.documentElement.style.overflow = "";
      this.activeSlotIndex = null;

      if (this.lastFocus && typeof this.lastFocus.focus === "function") this.lastFocus.focus();
    }

    isModalOpen() {
      return this.modal && !this.modal.hasAttribute("hidden");
    }

    renderModalList(resetScroll = false) {
      const q = (this.modalSearch?.value || "").trim().toLowerCase();
      const filterCol = (this.activeCollectionFilter || "").trim().toLowerCase();

      let filtered = this.products;

      if (filterCol) {
        filtered = filtered.filter(
          (p) => String(p.collection_title || "").trim().toLowerCase() === filterCol
        );
      }

      if (q.length) {
        filtered = filtered.filter((p) => {
          const hay = `${p.title} ${p.vendor || ""} ${p.collection_title || ""}`.toLowerCase();
          return hay.includes(q);
        });
      }

      // Sort: slutsålda produkter visas alltid sist
      filtered = filtered.slice().sort((a, b) => {
        const aOut = this.stockStatus(a) === "sold_out" ? 1 : 0;
        const bOut = this.stockStatus(b) === "sold_out" ? 1 : 0;
        return aOut - bOut;
      });

      if (this.modalMeta)
        this.modalMeta.textContent = `${filtered.length} result${filtered.length === 1 ? "" : "s"}`;
      if (resetScroll && this.modalList) this.modalList.scrollTop = 0;

      this.modalList.innerHTML = "";

      if (!filtered.length) {
        this.modalList.innerHTML = `<div style="grid-column:1/-1;color:rgba(15,15,18,.62);padding:8px 4px;">Inga parfymer matchar din sökning.</div>`;
        return;
      }

      const slotIndex = this.activeSlotIndex;
      const frag = document.createDocumentFragment();

      for (const p of filtered) {
        const original = p.price;
        const discounted = this.calcDiscountedCents(original);

        const status = this.stockStatus(p);
        const max = this.maxQtyForProduct(p);
        const used = this.countSelected(p.variant_id, slotIndex);
        const remaining = max === Infinity ? Infinity : Math.max(0, max - used);

        // sold_out → always disabled; preorder → always pickable; in_stock → check remaining
        const canPick = status === "sold_out"
          ? false
          : remaining === Infinity ? true : remaining >= 1;
        const showDiscount = discounted < original;

        // Badge: sold_out → "Slutsåld", preorder → "Beställningsvara", low → "X kvar", else none
        let stockText = "";
        let stockMod = "";
        if (status === "sold_out") {
          stockText = "Slutsåld";
          stockMod = "bb3x__stockTag--soldout";
        } else if (status === "preorder") {
          stockText = "Beställningsvara";
          stockMod = "bb3x__stockTag--preorder";
        } else if (remaining !== Infinity && remaining <= 3) {
          stockText = `${remaining} kvar`;
          stockMod = "bb3x__stockTag--low";
        }

        const card = document.createElement("button");
        card.type = "button";
        card.className = "bb3x__card";
        if (!canPick) card.disabled = true;

        const imgHtml = p.image
          ? `<img loading="lazy" src="${p.image}" alt="${this.escape(p.title)}">`
          : `<span class="bb3x__imgPh" aria-hidden="true"></span>`;

        card.innerHTML = `
          <div class="bb3x__cardImg">${imgHtml}</div>
          <div class="bb3x__cardBody">
            <div class="bb3x__cardTitle">${this.escape(p.title)}</div>
            <div class="bb3x__cardSub">
              ${this.escape(p.vendor || "")}${p.collection_title ? ` • ${this.escape(p.collection_title)}` : ""}
            </div>
            ${stockText ? `<div class="bb3x__stockTag ${stockMod}">${stockText}</div>` : ""}
          </div>
          <div class="bb3x__cardRight">
            <div class="bb3x__prices">
              ${showDiscount ? `<span class="bb3x__priceOld">${money(original)}</span>` : ""}
              <span class="bb3x__priceNew">${money(showDiscount ? discounted : original)}</span>
            </div>
          </div>
        `;

        card.addEventListener("click", () => {
          if (!this.canSelectProduct(p, slotIndex)) {
            const maxNow = this.maxQtyForProduct(p);
            this.flashStatus(`Du kan max välja ${maxNow} st av "${p.title}" (lager).`);
            return;
          }
          this.pickProductForSlot(p);
        });

        frag.appendChild(card);
      }

      this.modalList.appendChild(frag);
    }

    pickProductForSlot(p) {
      const idx = this.activeSlotIndex;
      if (idx == null) return;

      if (!this.canSelectProduct(p, idx)) {
        const max = this.maxQtyForProduct(p);
        this.flashStatus(`Du kan max välja ${max} st av "${p.title}" (lager).`);
        return;
      }

      const currentEl = Array.from(this.slotsWrap.children).find(
        (el) => el.getAttribute("data-index") === String(idx)
      );
      if (!currentEl) return;

      this.selected[idx] = {
        variant_id: p.variant_id,
        title: p.title,
        image: p.image,
        price: p.price,
        vendor: p.vendor,
        url: p.url,
        inventory_quantity: p.inventory_quantity,
        inventory_management: p.inventory_management,
        inventory_policy: p.inventory_policy,
      };

      const requiredBadge = idx < 3 ? "Required" : "Optional";
      const original = p.price;
      const discounted = this.calcDiscountedCents(original);
      const showDiscount = discounted < original;

      const selectedCard = document.createElement("div");
      selectedCard.className = "bb3x__selected";
      selectedCard.setAttribute("data-index", String(idx));

      const thumbHtml = p.image
        ? `<img loading="lazy" src="${p.image}" alt="${this.escape(p.title)}">`
        : `<span class="bb3x__imgPh" aria-hidden="true"></span>`;

      selectedCard.innerHTML = `
        <div class="bb3x__thumb">${thumbHtml}</div>
        <div>
          <div class="bb3x__selTitle">${this.escape(p.title)}</div>
          <div class="bb3x__selMeta">${this.escape(p.vendor || "")} • <span style="color:var(--bb-accent);font-weight:750;">${requiredBadge}</span></div>
        </div>
        <div style="display:grid;justify-items:end;gap:10px;">
          <div class="bb3x__prices">
            ${showDiscount ? `<span class="bb3x__priceOld">${money(original)}</span>` : ""}
            <span class="bb3x__priceNew">${money(showDiscount ? discounted : original)}</span>
          </div>
          <button class="bb3x__remove" type="button" aria-label="Remove">✕</button>
        </div>
      `;

      selectedCard.querySelector(".bb3x__remove").addEventListener("click", (e) => {
        e.stopPropagation();
        this.removeSelection(idx);
      });

      selectedCard.addEventListener("click", () => this.openPicker(idx));
      this.slotsWrap.replaceChild(selectedCard, currentEl);

      this.closeModal();
      this.updateState();
      this.flashStatus(`Vald: ${p.title}`);
    }

    removeSelection(idx) {
      this.selected[idx] = null;

      const selectedEl = Array.from(this.slotsWrap.children).find(
        (el) =>
          el.classList.contains("bb3x__selected") &&
          el.getAttribute("data-index") === String(idx)
      );
      if (!selectedEl) return;

      const isRequired = idx < 3;

      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "bb3x__slot";
      btn.setAttribute("data-bb3x-slot", "");
      btn.setAttribute("data-index", String(idx));
      btn.setAttribute("aria-haspopup", "dialog");

      btn.innerHTML = `
        <span class="bb3x__slotInner">
          <span class="bb3x__slotTop">
            <span class="bb3x__slotLabel">Parfym</span>
            <span class="bb3x__slotReq">${isRequired ? "Required" : "Optional"}</span>
          </span>
          <span class="bb3x__slotCta">Välj</span>
          <span class="bb3x__slotHint">Tryck för att välja doft</span>
        </span>
      `;

      btn.addEventListener("click", () => this.openPicker(idx));
      this.slotsWrap.replaceChild(btn, selectedEl);

      this.updateState();
      this.flashStatus("Borttagen.");
    }

    getChosenItems() {
      return this.selected.filter(Boolean);
    }

    updateTotals() {
      const items = this.getChosenItems();
      let totalOld = 0;
      let totalNew = 0;

      for (const it of items) {
        const p = typeof it.price === "number" ? it.price : 0;
        totalOld += p;
        totalNew += this.calcDiscountedCents(p);
      }

      const showDiscount = totalNew < totalOld && items.length >= 3;

      if (this.totalOldEl) {
        if (showDiscount) {
          this.totalOldEl.hidden = false;
          this.totalOldEl.textContent = money(totalOld);
        } else {
          this.totalOldEl.hidden = true;
          this.totalOldEl.textContent = "";
        }
      }
      if (this.totalNewEl) {
        this.totalNewEl.textContent = money(items.length >= 3 ? totalNew : totalOld);
      }
    }

    updateState() {
      const items = this.getChosenItems();
      const count = items.length;
      const missing = clamp(3 - count, 0, 3);

      if (this.addToCartBtn) this.addToCartBtn.disabled = count < 3;

      if (this.statusEl) {
        if (!this.products.length) {
          this.statusEl.textContent = "Inga produkter hittades i valda kollektioner.";
        } else {
          this.statusEl.textContent =
            count < 3
              ? `Välj ${missing} till för att lägga i varukorg. (${count}/3 valda)`
              : `${count} valda. Redo att lägga i varukorg.`;
        }
      }

      this.updateTotals();
    }

    buildCartItemsValidated() {
      const items = this.getChosenItems();
      const map = new Map();

      for (const it of items) {
        const key = String(it.variant_id);
        const prev = map.get(key);
        map.set(key, { it, qty: (prev ? prev.qty : 0) + 1 });
      }

      for (const { it, qty } of map.values()) {
        const max = this.maxQtyForProduct(it);
        if (max !== Infinity && qty > max) {
          return {
            ok: false,
            message: `Inte tillräckligt lager för "${it.title}". Max: ${max}.`,
          };
        }
      }

      return {
        ok: true,
        items: Array.from(map.values()).map(({ it, qty }) => ({
          id: it.variant_id,
          quantity: qty,
          properties: { _bundle: "3x Bundle Builder", _discount: "BUNDLEDEAL" },
        })),
      };
    }

    async addToCart() {
      const chosen = this.getChosenItems();
      if (chosen.length < 3) {
        this.flashStatus("Välj minst 3 parfymer.");
        return;
      }

      const validated = this.buildCartItemsValidated();
      if (!validated.ok) {
        this.flashStatus(validated.message);
        return;
      }

      this.addToCartBtn.disabled = true;
      const originalText = this.addToCartBtn.textContent;
      this.addToCartBtn.textContent = "Lägger till…";

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          headers: { "Content-Type": "application/json", Accept: "application/json" },
          body: JSON.stringify({ items: validated.items }),
        });

        if (!res.ok) {
          const t = await res.text();
          throw new Error(t || "Add to cart failed");
        }

        this.flashStatus("Tillagd i varukorg.");
        document.dispatchEvent(new CustomEvent("cart:refresh", { bubbles: true }));
        document.dispatchEvent(new CustomEvent("cart:open", { bubbles: true }));

        setTimeout(() => (window.location.href = "/cart"), 650);
      } catch (e) {
        console.warn("[bb3x] addToCart error", e);
        this.flashStatus("Kunde inte lägga till. Försök igen.");
      } finally {
        this.addToCartBtn.textContent = originalText;
        this.addToCartBtn.disabled = this.getChosenItems().length < 3;
      }
    }

    flashStatus(msg) {
      if (!this.statusEl) return;
      this.statusEl.textContent = msg;
    }

    escape(str) {
      return String(str || "")
        .replaceAll("&", "&amp;")
        .replaceAll("<", "&lt;")
        .replaceAll(">", "&gt;")
        .replaceAll('"', "&quot;")
        .replaceAll("'", "&#039;");
    }
  }

  const mount = () => {
    document.querySelectorAll("[data-bb3x]").forEach((root) => {
      if (root.__bb3xMounted) return;
      root.__bb3xMounted = true;
      new BundleBuilder3X(root);
    });
  };

  if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", mount);
  else mount();

  document.addEventListener("shopify:section:load", mount);
})();
